import { logger } from '@porsche-code/kado-logger/client';
import { record } from 'aws-amplify/analytics/kinesis-firehose';
import { sha256 } from 'crypto-hash';

declare global {
  interface Window {
    Cypress?: unknown;
    __E2E__?: boolean;
  }
}

type ConfigProps = {
  appName: string;
  appVersion: string;
  disabled?: boolean;
};

type PersonProps = {
  id: number;
  organizationId?: number;
  role?: string;
};

export enum AnalyticsTrackEvent {
  Login = 'LOGIN',
  OfflineMode = 'OFFLINE_MODE',
  OpenChangeHistory = 'OPEN_CHANGE_HISTORY',
}
export type AnalyticsTrackEventProperties = Record<string, string>;

class AnalyticsClass {
  private config?: ConfigProps;
  private person?: PersonProps;

  get userHash() {
    if (!this.person) {
      throw new Error(
        'Person not identified. Call Analytics.identify() first.',
      );
    }

    return sha256(this.person.id.toString());
  }

  public configure(config: ConfigProps) {
    this.config = config;

    logger.debug(config, 'Analytics#configure');

    return this.config;
  }

  public identify(person: PersonProps) {
    this.person = person;
  }

  public async track(
    event: AnalyticsTrackEvent,
    eventProperties?: AnalyticsTrackEventProperties,
  ) {
    if (!this.config) {
      throw new Error(
        'Analytics is not configured. Call Analytics.configure() first.',
      );
    }

    const { appName } = this.config;

    const data: Record<string, unknown> | Uint8Array = {
      event,
      properties: eventProperties,
      app_name: appName,
      event_timestamp: Date.now() / 1000,
      ...(this.person && {
        user_hash: await this.userHash,
        ...(this.person.organizationId && {
          organization_id: this.person.organizationId,
        }),
        ...(this.person.role && { role: this.person.role }),
      }),
    };

    if (
      process.env.NODE_ENV !== 'production' ||
      window.Cypress ||
      window.__E2E__
    ) {
      logger.debug(data, 'Analytics#track');
      return;
    }

    return record({
      data,
      streamName: `analytics-stream-${process.env.REACT_APP_ENVIRONMENT_SLUG}`,
    });
  }
}

export const Analytics = new AnalyticsClass();
